import React, {useState} from 'react'
import {
  Button,
  ButtonGroup,
  ButtonProps,
  Tooltip
} from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'
import {
  CheckCircle,
  Check,
  Sync
} from '@mui/icons-material'
import { LoadingButton } from '../../_EOS/Button/LoadingButton'
import { ConfirmAction } from '../../ConfirmAction'
import { Modal } from '../../_EOS/Modal'

interface ProductValidationListProps extends ButtonProps {
  text: string
  success: boolean
  closeProductValidation: () => void
  resetProductValidation: () => void
  subtitle: string
  isLoading: boolean
  isValidated: boolean
}

export const ProductValidationList: React.FC<ProductValidationListProps> = ({
  text,
  subtitle,
  success,
  closeProductValidation,
  resetProductValidation,
  onClick,
  isLoading,
  isValidated
}) => {
  const classes = useStyles()
  const [Loading, setIsLoading] = useState<boolean>(false)
  const [confirmConfig, setConfirmConfig] = useState<{
    title: string
    confirmText: string
    negateText: string
    onConfirm: () => void
    open: boolean
  }>({
    title: '',
    confirmText: '',
    negateText: '',
    onConfirm: () => { },
    open: false
  })

  const openConfirm = (
    title: string,
    confirmText: string,
    negateText: string,
    onConfirm: () => void
  ) => {
    setConfirmConfig({
      title,
      confirmText,
      negateText,
      onConfirm,
      open: true,
    })
  }

  const isComplete = success
  const isPartial = isValidated && !success;

  const handleCloseConfirm = () => {
    setIsLoading(true)
    setConfirmConfig((prev) => ({ ...prev, open: false }))
    setIsLoading(false)
  }

  const handleSaveClick = () => {
    openConfirm(
      '¿Al cerrar la validación se guardarán los cambios con las unidades incompletas?',
      'Aceptar',
      'Cancelar',
      () => {
        setConfirmConfig((prev) => ({ ...prev, open: false }))
        closeProductValidation()
      }
    )
  }

  const handleResetClick = () => {
    openConfirm(
      `¿Está seguro que desea reiniciar la validación del producto ${text}?`,
      'Aceptar',
      'Cancelar',
      () => {
        setConfirmConfig((prev) => ({ ...prev, open: false }))
        resetProductValidation()
      }
    )
  }

  return (
    <div>
      <ButtonGroup
        disabled={isLoading}
        className={classes.buttonGroup}
        variant='contained'
        color='primary'
        aria-label='contained primary button group'
      >
        <Button
          variant='outlined'
          className={classes.buttonLarge}
          onClick={onClick}
        >
          {text} - {subtitle}
        </Button>
        {isComplete && (
        <CheckCircle className={classes.completeIcon} />
      )}

      {isPartial && (
        <CheckCircle className={classes.partialIcon} />
      )}

      {!isValidated && (
        <LoadingButton
          loading={isLoading}
          className={classes.buttonSave}
          startIcon={<Check />}
          onClick={handleSaveClick}
        />
      )}

      {isValidated && (
        <Tooltip title='Reiniciar Producto' aria-label='Reiniciar Validación'>
          <span className={classes.spanReset}>
            <LoadingButton
              loading={isLoading}
              className={classes.buttonReset}
              startIcon={<Sync />}
              onClick={handleResetClick}
              disabled={!isValidated}
            />
          </span>
        </Tooltip>
      )}
      </ButtonGroup>
      <Modal
        onClose={handleCloseConfirm}
        open={confirmConfig.open}
      >
        <ConfirmAction
          handleCloseModal={handleCloseConfirm}
          handleSubmitAction={confirmConfig.onConfirm}
          confirmTitle={confirmConfig.title}
          confirmText={confirmConfig.confirmText}
          negateText={confirmConfig.negateText}
          isLoading={Loading}
        />
      </Modal>
    </div>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    buttonGroup: {
      width: '100%',
      margin: '3px 0'
    },
    buttonLarge: {
      width: '90%',
      color: theme.palette.common.black,
      padding: theme.spacing(1, 4),
      '&:hover': {
        backgroundColor: '#e0e4e8'
      },
      textTransform: 'none',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    buttonSave: {
      width: '10%',
      justifyContent: 'center'
    },
    completeIcon: {
      color: '#4CAF50',
      width: 'auto',
      alignSelf: 'center',
      borderRight: 'none !important'
    },
    buttonReset: {
      width: 'auto',
      justifyContent: 'end'
    },
    partialIcon: {
      color: '#FFC200',
      width: 'auto',
      alignSelf: 'center',
      borderRight: 'none !important'    
    },
    spanReset: {
      display: 'flex',
      justifyContent: 'center'
    }    
  })
)
